import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Download, XCircle, CheckCircle, Clock, CalendarDays, Eye, FileCheck2, MapPin, ShieldCheck, X, Phone, Mail, Receipt, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';
import Dialog from '../../components/Dialog';
import { getDocumentUrl } from '../../utils/documentUrl';
import { TEXTAREA_WORD_LIMIT, countWords, limitWords } from '../../utils/wordLimits';
import { getAdmissionTypeLabel } from '../../utils/admissionType';

const progressSteps = [
  { key: 'applied', label: 'Applied', icon: Clock },
  { key: 'doc_review', label: 'Documents Verified', icon: FileCheck2 },
  { key: 'doc_review_rejected', label: 'Documents Not Verified', icon: XCircle },
  { key: 'document_updated', label: 'Pending Document Update', icon: FileText },
  { key: 'campus_visit', label: 'Campus Visit', icon: MapPin },
  { key: 'confirmed', label: 'Confirmation Status', icon: ShieldCheck },
  { key: 'rejected', label: 'Rejected', icon: XCircle },
  { key: 'cancelled', label: 'Cancelled', icon: XCircle },
];

const statusOptionsList = [
  { key: 'submitted', label: 'Applied' },
  { key: 'doc_review', label: 'Documents Verified' },
  { key: 'doc_review_rejected', label: 'Documents Not Verified' },
  { key: 'document_updated', label: 'Pending Document Update' },
  { key: 'campus_visit', label: 'Completed Campus Visit' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
];

const getLatestStatusKey = (app = {}) => {
  if (['confirmed', 'rejected', 'cancelled'].includes(app.status)) return app.status;
  return app.displayAdmissionStage || app.admissionStage || app.status || 'submitted';
};

const formatActorLabel = (actor = {}) => {
  const name = String(actor.name || actor.email || '').trim();
  if (name) return name;
  const role = String(actor.role || '').replace(/_/g, ' ').trim();
  return role ? role.replace(/\b\w/g, (char) => char.toUpperCase()) : 'Admin';
};

const getDocumentSource = (doc = {}) => {
  const meta = doc?.metadata && typeof doc.metadata === 'object' ? doc.metadata : {};
  return meta.source || doc?.source || 'application';
};

const isPendingPostPaymentDocument = (doc = {}) => (
  getDocumentSource(doc) === 'profile_document_update'
  && !['accepted', 'rejected'].includes(doc?.status || 'uploaded')
);

const getAdmissionPathway = (app = {}) => {
  const admissionType = String(app.admissionType || '').trim();
  if (/lateral/i.test(admissionType)) {
    return { label: 'Lateral Entry', detail: app.currentQualification || 'Applying via lateral entry' };
  }
  const qualification = String(app.currentQualification || '').trim();
  if (/^lateral/i.test(qualification)) {
    return { label: 'Lateral Entry', detail: qualification };
  }
  if (qualification) {
    return { label: 'Regular', detail: qualification };
  }
  return { label: getAdmissionTypeLabel(admissionType), detail: qualification || '-' };
};

const getLatestStatusLabel = (app = {}) => statusOptionsList.find((item) => item.key === getLatestStatusKey(app))?.label || 'Applied';

const guessFilename = (doc = {}) => {
  const name = String(doc.originalName || '').trim();
  if (name) return name;
  const key = String(doc.documentKey || 'document').replace(/\s+/g, '_');
  const url = String(doc.url || '');
  const ext = (url.match(/\.([a-z0-9]{2,6})(?:\?|#|$)/i)?.[1] || '').toLowerCase();
  return ext ? `${key}.${ext}` : key;
};

const handleDownload = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed (${response.status})`);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

const canVerifyDocumentsForAdmission = (admission = {}) => {
  const documentRecords = Array.isArray(admission.documentRecords) ? admission.documentRecords : [];
  const profileDocumentRecords = Array.isArray(admission.profileDocumentRecords)
    ? admission.profileDocumentRecords
    : Object.entries(admission.docs || {}).map(([documentKey, url]) => ({
        documentKey,
        url,
        createdAt: admission.createdAt,
        status: 'uploaded',
      }));

  const latestDocumentByKey = [...documentRecords, ...profileDocumentRecords]
    .filter((doc) => doc.url)
    .reduce((acc, doc) => {
      const key = String(doc.documentKey || doc.url || '').replace(/^doc_/, '');
      const current = acc[key];
      const docTime = new Date(doc.createdAt || 0).getTime();
      const currentTime = new Date(current?.createdAt || 0).getTime();
      if (!current || docTime >= currentTime) acc[key] = { ...doc, documentKey: key };
      return acc;
    }, {});

  const latest = Object.values(latestDocumentByKey);
  return latest.length > 0 && latest.every((doc) => doc.status === 'accepted');
};

const ADMISSIONS_FILTER_SESSION_KEY = 'seatifyai.adminAdmissions.filters';

const getStoredAdmissionFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ADMISSIONS_FILTER_SESSION_KEY) || '{}');
  } catch {
    return {};
  }
};

const storeAdmissionFilters = (filters) => {
  try {
    sessionStorage.setItem(ADMISSIONS_FILTER_SESSION_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage failures; URL params still preserve current navigation.
  }
};

const truncate = (value = '', max = 28) => {
  const text = String(value || '');
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 3))}...`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export default function AdminAdmissions({ apiBase = '/api/admin' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const storedFilters = useMemo(() => getStoredAdmissionFilters(), []);
  const { applicationId } = useParams();
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [pendingStep, setPendingStep] = useState(null);
  const [savingStep, setSavingStep] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  const [documentRejectTarget, setDocumentRejectTarget] = useState(null);
  const [documentRejectReason, setDocumentRejectReason] = useState('');
  const [savingDocumentStatus, setSavingDocumentStatus] = useState(false);
  const [statusChoice, setStatusChoice] = useState('');
  const [seenStudentChecked, setSeenStudentChecked] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('district') || searchParams.get('q') || storedFilters.q || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || storedFilters.status || 'all');
  const [paymentFilter, setPaymentFilter] = useState(searchParams.get('payment') || storedFilters.payment || 'completed');
  const [institutionFilter, setInstitutionFilter] = useState(searchParams.get('institutionId') || storedFilters.institutionId || 'all');
  const [institutions, setInstitutions] = useState([]);
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || storedFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || storedFilters.dateTo || '');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const isScopedAdmin = apiBase === '/api/institution-admin' || user?.role === 'cluster_admin';

  const admissionsBasePath = useMemo(() => (
    location.pathname.startsWith('/institution') ? '/institution/admissions' : '/admin/admissions'
  ), [location.pathname]);

  const backToAdmissions = () => {
    const qs = searchParams.toString();
    navigate(qs ? `${admissionsBasePath}?${qs}` : admissionsBasePath);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 2000);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, paymentFilter, institutionFilter, dateFrom, dateTo]);

  useEffect(() => {
    const stored = getStoredAdmissionFilters();
    setSearchTerm(searchParams.get('district') || searchParams.get('q') || stored.q || '');
    setStatusFilter(searchParams.get('status') || stored.status || 'all');
    setPaymentFilter(searchParams.get('payment') || stored.payment || 'completed');
    setInstitutionFilter(searchParams.get('institutionId') || stored.institutionId || 'all');
    setDateFrom(searchParams.get('dateFrom') || stored.dateFrom || '');
    setDateTo(searchParams.get('dateTo') || stored.dateTo || '');
  }, [searchParams]);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'cluster_admin') return;
    const loadInstitutions = async () => {
      try {
        const res = await axios.get('/api/admin/institutions?export=true');
        setInstitutions(res.data && Array.isArray(res.data.items) ? res.data.items : []);
      } catch {
        setInstitutions([]);
      }
    };
    loadInstitutions();
  }, [user?.role]);

  useEffect(() => {
    if (!applicationId) return;
    const fetchAdmissionDetail = async () => {
      try {
        const res = await axios.get(`${apiBase}/admissions/${applicationId}`);
        setSelectedAdmission(res.data);
      } catch (err) {
        toast.error('Failed to load admission details');
      }
    };
    fetchAdmissionDetail();
  }, [apiBase, applicationId]);

  useEffect(() => {
    if (!selectedAdmission) return;
    setStatusChoice(getLatestStatusKey(selectedAdmission));
  }, [selectedAdmission?.applicationId]);

  const updateParams = (next = {}) => {
    const merged = {
      status: statusFilter,
      payment: paymentFilter,
      dateFrom,
      dateTo,
      q: searchTerm,
      institutionId: institutionFilter,
      ...next,
    };
    const params = {};
    Object.entries(merged).forEach(([key, value]) => {
      if (value && value !== 'all') params[key] = value;
    });
    storeAdmissionFilters({
      status: merged.status || 'all',
      payment: merged.payment || 'completed',
      dateFrom: merged.dateFrom || '',
      dateTo: merged.dateTo || '',
      q: merged.q || '',
      institutionId: merged.institutionId || 'all',
    });
    setSearchParams(params);
  };

  const fetchAdmissions = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };
      if (debouncedSearch) params.q = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (paymentFilter !== 'all') params.payment = paymentFilter;
      if (['admin', 'cluster_admin'].includes(user?.role) && apiBase === '/api/admin' && institutionFilter !== 'all') params.institutionId = institutionFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await axios.get(`${apiBase}/admissions`, { params });
      const payload = Array.isArray(res.data)
        ? { items: res.data, total: res.data.length }
        : (res.data || {});
      setAdmissions(payload.items || []);
      setTotalPages(Math.ceil((payload.total || 0) / limit) || 1);
      setTotal(payload.total || 0);
    } catch (err) {
      toast.error('Failed to load admissions data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmissions();
  }, [page, debouncedSearch, statusFilter, paymentFilter, institutionFilter, dateFrom, dateTo]);

  const exportCSV = async () => {
    try {
      const params = { export: 'true' };
      if (debouncedSearch) params.q = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (paymentFilter !== 'all') params.payment = paymentFilter;
      if (['admin', 'cluster_admin'].includes(user?.role) && apiBase === '/api/admin' && institutionFilter !== 'all') params.institutionId = institutionFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const res = await axios.get(`${apiBase}/admissions`, { params });
      const itemsToExport = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      if (itemsToExport.length === 0) return toast.error('No data to export');
      const headers = isScopedAdmin
        ? ['Application ID', 'Student', 'Email', 'Institution', 'Course', 'Program', 'Course Fee', 'Payment', 'Status', 'Applied On']
        : ['Application ID', 'Student', 'Email', 'Institution', 'Course', 'Program', 'Base Fee', 'Platform Fee', 'Total Paid', 'Payment', 'Status', 'Applied On'];
      const rows = itemsToExport.map(app => [
        app.applicationId, app.fullName || app.student?.name || '', app.email || app.student?.email || '',
        app.collegeName, app.courseName, app.programName, app.baseFee || app.fee || 0,
        ...(isScopedAdmin ? [] : [app.platformFee || 0, app.totalPaid || 0]),
        app.paymentStatus, app.status, app.createdAt ? new Date(app.createdAt).toLocaleDateString('en-IN') : ''
      ]);
      const csv = [headers, ...rows].map(row => row.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `seatify_admissions_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed': return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><CheckCircle size={12}/> Confirmed</span>;
      case 'doc_review': return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><FileCheck2 size={12}/> Documents Verified</span>;
      case 'doc_review_rejected': return <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><XCircle size={12}/> Docs Not Verified</span>;
      case 'document_updated': return <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><FileText size={12}/>Doc Updated</span>;
      case 'campus_visit': return <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><MapPin size={12}/> Campus Visit Complete</span>;
      case 'rejected': return <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><XCircle size={12}/> Rejected</span>;
      case 'cancelled': return <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><XCircle size={12}/> Cancelled</span>;
      default: return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs font-bold flex items-center gap-1 w-max"><Clock size={12}/> Pending</span>;
    }
  };

  const mergeUpdatedAdmission = (updated) => {
    setAdmissions((prev) => prev.map((item) => (item._id === updated._id || item.applicationId === updated.applicationId ? updated : item)));
    setSelectedAdmission(updated);
  };

  const confirmProgressChange = async () => {
    if (!selectedAdmission || !pendingStep) return;
    if (['rejected', 'cancelled'].includes(pendingStep) && !statusReason.trim()) {
      toast.error(pendingStep === 'cancelled' ? 'Cancellation reason is required' : 'Rejection reason is required');
      return;
    }
    if (pendingStep === 'campus_visit' && !seenStudentChecked) {
      toast.error('Please confirm that you have seen the student');
      return;
    }
    setSavingStep(true);
    try {
      const res = await axios.patch(`${apiBase}/admissions/${selectedAdmission.applicationId}/progress`, {
        step: pendingStep,
        seenStudent: pendingStep === 'campus_visit' ? seenStudentChecked : undefined,
        reason: statusReason.trim(),
      });
      mergeUpdatedAdmission(res.data.application);
      toast.success('Admission status updated');
      setPendingStep(null);
      setStatusReason('');
      setSeenStudentChecked(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update admission status');
    } finally {
      setSavingStep(false);
    }
  };

  const updateDocumentStatus = async (doc, status, reason = '') => {
    if (!selectedAdmission) return;
    if (status === 'rejected' && !String(reason || '').trim()) return toast.error('Rejection reason is required');
    setSavingDocumentStatus(true);
    try {
      const documentId = doc._id || doc.id || doc.documentKey;
      const res = await axios.patch(`${apiBase}/admissions/${selectedAdmission.applicationId}/documents/${documentId}/status`, {
        status,
        documentKey: doc.documentKey,
        originalName: doc.originalName,
        url: doc.url,
        reason,
      });
      mergeUpdatedAdmission(res.data.application);
      toast.success(`Document ${status}`);
      setDocumentRejectTarget(null);
      setDocumentRejectReason('');
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${status} document`);
    } finally {
      setSavingDocumentStatus(false);
    }
  };

  const canEditAdmissions = hasPermission(user, PERMISSIONS.ADMISSIONS_EDIT);
  const timeline = selectedAdmission?.admissionTimeline || {};
  const isLocked = Boolean(timeline.rejected || timeline.cancelled || ['rejected', 'cancelled'].includes(selectedAdmission?.status));
  const isConfirmedFinal = Boolean(timeline.confirmed || selectedAdmission?.status === 'confirmed');
  const selectedAdmissionIsPaid = selectedAdmission?.paymentStatus === 'completed';
  const canMarkStep = (step, options = {}) => {
    if (!canEditAdmissions || !selectedAdmissionIsPaid || isLocked) return false;
    if (step === 'cancelled') return isConfirmedFinal && !timeline.cancelled && selectedAdmission?.status !== 'cancelled';
    if (isConfirmedFinal) return false;
    if (timeline.confirmed && step === 'rejected') return false;
    if (timeline.rejected && step === 'confirmed') return false;
    if (!['doc_review', 'doc_review_rejected'].includes(step) && timeline[step]) return false;
    if (step === 'doc_review') return Boolean(options.canVerifyDocuments);
    if (step === 'doc_review_rejected') return true;
    if (step === 'campus_visit') return Boolean(timeline.doc_review);
    if (['confirmed', 'rejected'].includes(step)) return Boolean(timeline.campus_visit);
    return false;
  };
  const statusOptions = [
    { key: 'doc_review', label: 'Documents Verified' },
    { key: 'doc_review_rejected', label: 'Documents Not Verified' },
    { key: 'campus_visit', label: 'Campus Visit' },
    { key: 'confirmed', label: 'Confirm Admission' },
    { key: 'rejected', label: 'Reject Admission' },
    { key: 'cancelled', label: 'Cancel Application' },
  ].filter((option) => {
    if (timeline.confirmed && option.key === 'rejected') return false;
    if (timeline.rejected && option.key === 'confirmed') return false;
    if (option.key === 'cancelled') return isConfirmedFinal && !timeline.cancelled && selectedAdmission?.status !== 'cancelled';
    return true;
  });

  const statusDropdownWidthCh = useMemo(() => {
    const currentLabel = selectedAdmission ? getLatestStatusLabel(selectedAdmission) : 'Applied';
    const labels = [
      currentLabel,
      ...statusOptions.map((o) => o.label),
    ].map((l) => String(l || ''));

    const maxLen = labels.reduce((acc, l) => Math.max(acc, l.length), 0);
    // Add a small buffer; cap prevents UI from becoming too wide for extremely long labels.
    return Math.min(34, Math.max(14, maxLen + 3));
  }, [selectedAdmission?.applicationId, selectedAdmission?.status, statusOptions]);


  const selectedStatusOption = statusOptions.find((item) => item.key === statusChoice);
  const openStatusConfirm = () => {
    if (!statusChoice) return toast.error('Select a status to update');
    const currentKey = getLatestStatusKey(selectedAdmission);
    if (statusChoice === currentKey) return toast.error('Select a different status to update');
    if (!canMarkStep(statusChoice, { canVerifyDocuments: canVerifyDocumentsForAdmission(selectedAdmission) })) return toast.error('This status cannot be updated yet');
    if (statusChoice === 'campus_visit' && !seenStudentChecked) {
      toast.error('Please confirm that you have seen the student');
      return;
    }
    setStatusReason('');
    setPendingStep(statusChoice);
  };
  const campusVisitRequiresSeenStudent = selectedAdmission
    && statusChoice === 'campus_visit'
    && statusChoice !== getLatestStatusKey(selectedAdmission);

  const filtered = admissions;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${
            page === i
              ? 'z-10 bg-indigo-600 text-white focus-visible:outline-focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
              : 'text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-offset-0'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>;

  if (applicationId && selectedAdmission) {
    const detailTimeline = selectedAdmission.admissionTimeline || {};
    const documentRecords = selectedAdmission.documentRecords || [];
    const profileDocumentRecords = selectedAdmission.profileDocumentRecords || Object.entries(selectedAdmission.docs || {}).map(([documentKey, url]) => ({
      documentKey,
      url,
      version: 1,
      createdAt: selectedAdmission.createdAt,
      status: 'uploaded',
      source: 'user',
    }));
    const latestDocumentByKey = [...documentRecords, ...profileDocumentRecords]
      .filter((doc) => doc.url)
      .reduce((acc, doc) => {
        const key = String(doc.documentKey || doc.url || '').replace(/^doc_/, '');
        const current = acc[key];
        const docTime = new Date(doc.createdAt || 0).getTime();
        const currentTime = new Date(current?.createdAt || 0).getTime();
        if (!current || docTime >= currentTime) acc[key] = { ...doc, documentKey: key };
        return acc;
      }, {});
    const displayDocuments = Object.values(latestDocumentByKey)
      .sort((a, b) => String(a.documentKey || '').localeCompare(String(b.documentKey || '')) || new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    const canVerifyDocuments = displayDocuments.length > 0 && displayDocuments.every((doc) => doc.status === 'accepted');
    const getDocumentStatusBadge = (status = 'uploaded') => {
      if (status === 'accepted') return <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-black uppercase tracking-wide text-emerald-700">Accepted</span>;
      if (status === 'rejected') return <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-black uppercase tracking-wide text-rose-700">Rejected</span>;
      return <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-black uppercase tracking-wide text-amber-700">Pending</span>;
    };
    const admissionPathway = getAdmissionPathway(selectedAdmission);
	    const detailPaymentAmount = selectedAdmission.paymentStatus === 'completed'
	      ? (isScopedAdmin ? selectedAdmission.baseFee || selectedAdmission.fee : selectedAdmission.totalPaid)
	      : 0;
	    const detailFields = [
      ['Application ID', selectedAdmission.applicationId],
      ['Latest Status', getLatestStatusLabel(selectedAdmission)],
      // ['Admission Pathway', admissionPathway.label],
      ['Qualification', admissionPathway.detail],
      ['Student Name', selectedAdmission.fullName || selectedAdmission.student?.name],
      ['Email', selectedAdmission.email || selectedAdmission.student?.email],
      ['Mobile', selectedAdmission.mobile || selectedAdmission.student?.mobile],
	      ['Parent Name', selectedAdmission.parentName],
	      ['Parent Mobile', selectedAdmission.parentMobile],
	      ['Country', selectedAdmission.country || 'India'],
	      ['State', selectedAdmission.state],
	      ['District', selectedAdmission.district === 'Other' ? selectedAdmission.districtOther : selectedAdmission.district],
      ['Community', selectedAdmission.community === 'Others' ? selectedAdmission.communityOther : selectedAdmission.community],
      ['Aadhar', selectedAdmission.aadhar],
      ['Institution', selectedAdmission.collegeName],
      ['Course', selectedAdmission.courseName],
      ['Program', selectedAdmission.programName],
      ['Academic Year', selectedAdmission.academicYear],
      ['Payment ID', selectedAdmission.paymentId || selectedAdmission.payment?.razorpayPaymentId],
      ['Payment Status', selectedAdmission.paymentStatus],
	      [isScopedAdmin ? 'Course Fee Paid' : 'Total Paid', `₹${(detailPaymentAmount || 0).toLocaleString('en-IN')}`],
      ['Applied On', formatDateTime(selectedAdmission.createdAt)],
    ];
    const hasPendingDocumentUpdate = displayDocuments.some(isPendingPostPaymentDocument);
    const visibleSteps = progressSteps.filter((step) => {
      if (step.key === 'document_updated') return hasPendingDocumentUpdate;
      if (step.key === 'doc_review_rejected') return Boolean(detailTimeline.doc_review_rejected);
      if (step.key === 'confirmed' && (selectedAdmission.status === 'rejected' || detailTimeline.rejected)) return false;
      if (step.key === 'rejected') return selectedAdmission.status === 'rejected' || Boolean(detailTimeline.rejected);
      if (step.key === 'cancelled') return selectedAdmission.status === 'cancelled' || Boolean(detailTimeline.cancelled);
      return true;
    });

    const detailCanSubmitStatusUpdate = Boolean(statusChoice)
      && statusChoice !== getLatestStatusKey(selectedAdmission)
      && canMarkStep(statusChoice, { canVerifyDocuments })
      && (!campusVisitRequiresSeenStudent || seenStudentChecked);

    return (
      <div className="space-y-6">
        <header className="sticky top-0 z-30 -mx-1 px-1 pb-4 pt-1 bg-slate-50/95 backdrop-blur border-b border-slate-200">
          <button
            onClick={backToAdmissions}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-950 mb-4"
          >
            <X size={16} /> Back to admissions
          </button>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider">{selectedAdmission.applicationId}</p>
                <h1 className="text-2xl md:text-3xl font-black text-slate-900 mt-1 truncate" style={{ fontFamily: 'Poppins' }}>
                  {selectedAdmission.fullName || selectedAdmission.student?.name || 'Student Application'}
                </h1>
                <p className="text-sm text-slate-500 mt-2">{selectedAdmission.collegeName} · {selectedAdmission.courseName}</p>
              </div>
              <div className="shrink-0">{getStatusBadge(getLatestStatusKey(selectedAdmission))}</div>
            </div>

	            {canEditAdmissions && selectedAdmission.paymentStatus === 'completed' && !isLocked && (
              <div className="pt-5 border-t border-slate-100">
                <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider mb-3">Update Status</h3>
                <div className="flex flex-col gap-3 lg:flex-row   lg:items-end lg:flex-wrap">
                  <select
                    value={statusChoice}
                    onChange={(e) => {
                      setStatusChoice(e.target.value);
                      setSeenStudentChecked(false);
                    }}
                    style={{ minWidth: `${statusDropdownWidthCh}ch` }}
                    className="w-full lg:w-auto lg:min-w-0 px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value={getLatestStatusKey(selectedAdmission)} disabled>
                      Current: {getLatestStatusLabel(selectedAdmission)}
                    </option>
                    {statusOptions.map((option) => (
                      <option
                        key={option.key}
                        value={option.key}
                        disabled={!canMarkStep(option.key, { canVerifyDocuments })}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {campusVisitRequiresSeenStudent && (
                    <label className="flex items-start align-ceter lg:w-auto lg:min-w-0 gap-3 rounded-xl h-12 border border-violet-200 bg-violet-50 px-2 py-3 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={seenStudentChecked}
                        onChange={(e) => setSeenStudentChecked(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded accent-violet-600"
                      />
                      <span className="text-sm font-semibold text-slate-800">
                        Student seen on campus
                        {/* <span className="block text-xs font-medium text-slate-500 mt-0.5">Required for campus visit</span> */}
                      </span>
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={openStatusConfirm}
                    disabled={!detailCanSubmitStatusUpdate}
                    className="w-full lg:w-auto shrink-0 px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-800"
                  >
                    Update Status
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr] xl:items-start">
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-4">Documents</h2>
              {displayDocuments.length === 0 ? (
                <p className="text-sm text-slate-500">No documents found for this student.</p>
              ) : (
                <div className="grid gap-3">
                  {displayDocuments.map((doc) => {
                    const documentUrl = getDocumentUrl(doc.url);
                    return (
                      <div
                        key={doc._id || `${doc.documentKey}-${doc.version}`}
                        className="flex flex-col gap-3 rounded-xl border border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div>
	                          <div className="flex flex-wrap items-center gap-2">
	                            <p className="text-sm font-black text-slate-900">{String(doc.documentKey || '').replace(/_/g, ' ')}</p>
	                            {getDocumentStatusBadge(doc.status)}
	                            {isPendingPostPaymentDocument(doc) && (
	                              <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-black uppercase tracking-wide text-indigo-700">
	                                Pending Document Update
	                              </span>
	                            )}
	                          </div>
	                          <p className="text-xs text-slate-500 mt-1">
	                            Latest uploaded · {formatDateTime(doc.createdAt)}
	                          </p>
                          {doc.status === 'rejected' && doc.metadata?.reason && (
                            <p className="text-xs font-semibold text-rose-600 mt-1">Reason: {doc.metadata.reason}</p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <a
                            href={documentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                          >
                            <Eye size={14} /> View
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await handleDownload(documentUrl, guessFilename(doc));
                              } catch {
                                toast.error('Failed to download document');
                              }
                            }}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800"
                          >
                            <Download size={14} /> Download
                          </button>
	                          {canEditAdmissions && selectedAdmission.paymentStatus === 'completed' && !isLocked && doc.status !== 'accepted' && doc.status !== 'rejected' && (
                            <>
                              <button
                                type="button"
                                onClick={() => updateDocumentStatus(doc, 'accepted')}
                                className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-100"
                              >
                                <CheckCircle size={14} /> Accept
                              </button>
	                              <button
	                                type="button"
	                                onClick={() => {
	                                  setDocumentRejectTarget(doc);
	                                  setDocumentRejectReason('');
	                                }}
                                className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 hover:bg-rose-100"
                              >
                                <XCircle size={14} /> Reject
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-4">Student Application Details</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {detailFields.map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-100 p-3">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">{label}</p>
                    <p className="text-sm font-semibold text-slate-800 break-words">{value || '-'}</p>
                  </div>
                ))}
              </div>
            </div>

            
          </div>

          <div className="xl:sticky xl:top-52 xl:max-h-[calc(100vh-13rem)] xl:overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-4 sticky top-0 z-10 -mx-6 px-6 py-2 bg-white border-b border-slate-100">Admission Status</h2>
              <div className="space-y-4">
              {visibleSteps.map((step, index) => {
                const item = step.key === 'document_updated'
                  ? detailTimeline.document_updates?.[detailTimeline.document_updates.length - 1]
                  : detailTimeline[step.key];
                const StepIcon = step.icon;
                const completed = Boolean(item) || (step.key === 'confirmed' && selectedAdmission.status === 'confirmed') || (step.key === 'cancelled' && selectedAdmission.status === 'cancelled');
                return (
                  <div key={step.key} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${completed ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                        {completed ? <CheckCircle size={18} /> : <StepIcon size={18} />}
                      </div>
                      {index < visibleSteps.length - 1 && <div className={`w-px flex-1 min-h-10 ${completed ? 'bg-emerald-200' : 'bg-slate-200'}`} />}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="font-bold text-slate-900">{step.label}</p>
                      <p className="text-xs text-slate-500 mt-1">{item?.completedAt ? formatDateTime(item.completedAt) : step.key === 'applied' ? formatDateTime(selectedAdmission.createdAt) : 'Pending'}</p>
                      {step.key === 'campus_visit' && item?.seenStudent && <p className="text-xs font-bold text-emerald-600 mt-1">Seen student</p>}
                      {step.key === 'rejected' && item?.actor && (
                        <p className="text-xs font-bold text-rose-600 mt-1">Rejected by {formatActorLabel(item.actor)}</p>
                      )}
                      {step.key === 'rejected' && item?.reason && (
                        <p className="text-xs font-semibold text-slate-500 mt-1">Reason: {item.reason}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {Array.isArray(detailTimeline.doc_review_events) && detailTimeline.doc_review_events.length > 0 && (
              <div className="border-t border-slate-100 mt-5 pt-5">
                <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider mb-3">Document Review History</h3>
                <div className="space-y-2">
                  {detailTimeline.doc_review_events.map((event, index) => (
                    <div key={`${event.completedAt}-${index}`} className="rounded-xl bg-slate-50 p-3 text-xs">
                      <p className={`font-black ${event.status === 'rejected' ? 'text-rose-600' : 'text-emerald-600'}`}>{event.status === 'rejected' ? 'Rejected documents' : 'Confirmed documents'}</p>
                      <p className="text-slate-500 mt-1">{formatDateTime(event.completedAt)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            </div>
          </div>
        </div>

	        <Dialog
	          isOpen={Boolean(pendingStep)}
	          onClose={() => {
	            if (savingStep) return;
	            setPendingStep(null);
	            setStatusReason('');
	            setSeenStudentChecked(false);
	          }}
	          title="Confirm status update"
	          description={`Update ${selectedAdmission?.fullName || selectedAdmission?.student?.name || 'this student'} (${selectedAdmission?.applicationId || ''}) to ${selectedStatusOption?.label || pendingStep || 'selected status'}? This will be saved with the current date and time.`}
	          icon={<ShieldCheck className="text-indigo-600" size={22} />}
	        >
	          {pendingStep === 'campus_visit' && (
	            <label className="mb-4 flex items-start gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 cursor-pointer">
	              <input
	                type="checkbox"
	                checked={seenStudentChecked}
	                onChange={(e) => setSeenStudentChecked(e.target.checked)}
	                className="mt-0.5 h-4 w-4 rounded accent-violet-600"
	              />
	              <span className="text-sm font-semibold text-slate-800">I have seen the student on campus</span>
	            </label>
	          )}
	          {['rejected', 'cancelled'].includes(pendingStep) && (
	            <>
			            <textarea
			              value={statusReason}
			              onChange={(e) => setStatusReason(limitWords(e.target.value, TEXTAREA_WORD_LIMIT))}
		              placeholder={pendingStep === 'cancelled' ? 'Enter cancellation reason' : 'Enter application rejection reason'}
		              className="min-h-[110px] w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-rose-100"
		              autoFocus
		            />
			            <p className="mb-4 mt-1 text-right text-xs font-semibold text-slate-500">
			              {countWords(statusReason)}/{TEXTAREA_WORD_LIMIT} words
			            </p>
	            </>
			          )}
	          <div className="flex gap-3">
	            <button disabled={savingStep} onClick={() => { setPendingStep(null); setStatusReason(''); setSeenStudentChecked(false); }} className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-bold">Cancel</button>
	            <button
	              disabled={savingStep || (pendingStep === 'campus_visit' && !seenStudentChecked)}
	              onClick={confirmProgressChange}
	              className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-bold disabled:opacity-60"
	            >
	              {savingStep ? 'Saving...' : 'Confirm'}
	            </button>
	          </div>
	        </Dialog>
	        <Dialog
	          isOpen={Boolean(documentRejectTarget)}
	          onClose={() => {
	            if (savingDocumentStatus) return;
	            setDocumentRejectTarget(null);
	            setDocumentRejectReason('');
	          }}
	          title="Reject document"
	          description={`Enter why ${String(documentRejectTarget?.documentKey || 'this document').replace(/_/g, ' ')} is being rejected.`}
	          icon={<XCircle className="text-rose-600" size={22} />}
	        >
		          <textarea
		            value={documentRejectReason}
		            onChange={(e) => setDocumentRejectReason(limitWords(e.target.value, TEXTAREA_WORD_LIMIT))}
		            placeholder="Enter document rejection reason"
		            className="min-h-[110px] w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-rose-100"
		            autoFocus
		          />
		          <p className="mb-4 mt-1 text-right text-xs font-semibold text-slate-500">
		            {countWords(documentRejectReason)}/{TEXTAREA_WORD_LIMIT} words
		          </p>
	          <div className="flex gap-3">
	            <button
	              disabled={savingDocumentStatus}
	              onClick={() => { setDocumentRejectTarget(null); setDocumentRejectReason(''); }}
	              className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-bold"
	            >
	              Cancel
	            </button>
	            <button
	              disabled={savingDocumentStatus || !documentRejectReason.trim()}
	              onClick={() => updateDocumentStatus(documentRejectTarget, 'rejected', documentRejectReason)}
	              className="flex-1 py-3 rounded-xl bg-rose-600 text-white font-bold disabled:opacity-60"
	            >
	              {savingDocumentStatus ? 'Saving...' : 'Reject'}
	            </button>
	          </div>
	        </Dialog>
      </div>
	    );
  }

  return (
    <div style={{ zoom: 0.9 }}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        {/* <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>Admissions</h1>
          <p className="text-slate-500 mt-1">View and manage all student applications.</p>
        </div> */}
        <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur border border-slate-200 rounded-2xl p-3 flex flex-wrap gap-3 items-center">
          {['admin', 'cluster_admin'].includes(user?.role) && apiBase === '/api/admin' && (
            <select
              value={institutionFilter}
              onChange={(e) => { setInstitutionFilter(e.target.value); updateParams({ institutionId: e.target.value }); }}
              className="px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium text-slate-700 bg-white"
            >
              <option value="all">All Institutions</option>
              {institutions.map((inst) => (
                <option
                  key={inst.id || inst._id}
                  value={inst.id || inst._id}
                  title={inst.displayName || inst.collegeName || inst.name || inst.slug || (inst.id || inst._id)}
                >
                  {truncate(inst.displayName || inst.collegeName || inst.name || inst.slug || (inst.id || inst._id), 32)}
                </option>
              ))}
            </select>
          )}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); updateParams({ status: e.target.value }); }}
            className="px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium text-slate-700 bg-white"
          >
            <option value="all">All Statuses</option>
            {statusOptionsList.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
          
          <select
            value={paymentFilter}
            onChange={(e) => { setPaymentFilter(e.target.value); updateParams({ payment: e.target.value }); }}
            className="px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium text-slate-700 bg-white"
          >
            <option value="all">All Payments</option>
            <option value="completed">Paid</option>
            <option value="pending">Unpaid</option>
          </select>

          <div className="flex items-center gap-2">
            <CalendarDays size={16} className="text-slate-400" />
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); updateParams({ dateFrom: e.target.value }); }} className="px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium text-slate-700 bg-white" />
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); updateParams({ dateTo: e.target.value }); }} className="px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium text-slate-700 bg-white" />
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search ID, Name, Institution, District..." 
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); updateParams({ q: e.target.value, district: '' }); }}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none w-full sm:w-64"
            />
          </div>
          {hasPermission(user, PERMISSIONS.ADMISSIONS_EXPORT) && (
            <button onClick={exportCSV} className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-xl font-medium hover:bg-slate-800 transition-colors">
              <Download size={18} />
              Export CSV
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-sm">
                <th className="p-4 font-bold">Application ID</th>
                <th className="p-4 font-bold">Student Name</th>
                <th className="p-4 font-bold">Institution & Course</th>
                <th className="p-4 font-bold">Course Fee Paid</th>
                <th className="p-4 font-bold">Payment Status</th>
                <th className="p-4 font-bold">Contact</th>
                <th className="p-4 font-bold">Admission Status</th>
                <th className="p-4 font-bold">Applied On</th>
                <th className="p-4 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="p-8 text-center text-slate-500">No admissions found.</td></tr>
              ) : (
                filtered.map(app => {
                  const isPaid = app.paymentStatus === 'completed';
                  return (
                  <tr
                    key={app._id}
                    onClick={() => navigate(`${app.applicationId}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <td className="p-4 font-medium text-indigo-600">{app.applicationId}</td>
                    <td className="p-4">
                      <p className="font-bold text-slate-800">{app.fullName || app.student?.name || 'N/A'}</p>
                      {/* <p className="text-xs text-slate-500">{app.student?.id || app.studentId || ''}</p> */}
                    </td>
                    <td className="p-4">
                      <p className="font-bold text-slate-700">{app.collegeName}</p>
                      <p className="text-xs text-slate-500">{app.courseName} - {app.programName}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-bold text-slate-800">₹{Number(app.baseFee || app.fee || 0).toLocaleString('en-IN')}</p>
                      <p className="text-xs text-slate-500">Course fee</p>
                    </td>
	                    <td className="p-4">
	                      <p className={`font-bold flex items-center gap-1 ${isPaid ? 'text-emerald-600' : 'text-amber-600'}`}>
	                        <Receipt size={14} />
	                        {isPaid ? 'Paid' : 'Unpaid'}
		                      </p>
		                      <p className="text-xs text-slate-500">
		                        {app.paymentStatus}
		                      </p>
	                      {/* {app.paymentId || app.payment?.razorpayPaymentId ? (
	                        <p className="text-[11px] text-slate-400 font-mono mt-0.5">{app.paymentId || app.payment?.razorpayPaymentId}</p>
	                      ) : null} */}
	                    </td>
                    <td className="p-4">
                      <p className="text-sm text-slate-700 flex items-center gap-1"><Phone size={14} className="text-slate-400" /> {app.mobile || app.student?.mobile || '-'}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Mail size={14} className="text-slate-400" /> {app.email || app.student?.email || '-'}</p>
                    </td>
                    <td className="p-4">{getStatusBadge(getLatestStatusKey(app))}</td>
                    <td className="p-4 text-sm text-slate-500">
                      {formatDateTime(app.createdAt)}
                    </td>
                    <td className="p-4">
                      {isPaid ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${app.applicationId}`);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800"
                        >
                          <Eye size={14} /> Action
                        </button>
                      ) : (
                        <span className="text-xs font-bold text-slate-400">Unpaid</span>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-2xl shadow-sm">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-bold">{(page - 1) * limit + 1}</span> to{' '}
                <span className="font-bold">{Math.min(page * limit, total)}</span> of{' '}
                <span className="font-bold">{total}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-xl shadow-sm bg-white border border-slate-200 overflow-hidden" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-r border-slate-200"
                >
                  &larr;
                </button>
                {renderPageNumbers()}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-l border-slate-200"
                >
                  &rarr;
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
